import NoDataRow from '@/platform/common/components/NoDataRow/NoDataRow';
import { localDate, localDateToMinutes } from '@/platform/lib/utils';
import { useConfirmationStore } from '@/platform/stores/useConfirmationStore';
import {
    Badge,
    Button,
    Segment,
    SegmentContent,
    SegmentHeader,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
} from '@/platform/ui';
import StatusIcon from '@/platform/views/Doelgroepregister/components/StatusIcon';
import { ArrowDownTrayIcon, CpuChipIcon } from '@heroicons/react/24/outline';
import { router, usePage } from '@inertiajs/react';
import DgrForm from './components/DgrForm';
import EmployeeShowLayout from './layouts/EmployeeShowLayout';

const DgrView = () => {
    const { auth, employee } = usePage().props;
    const confirm = useConfirmationStore(state => state.confirm);

    const canDownload = auth?.permissions?.includes('download dgr screenshot');
    const checks = employee?.dgr?.checks ?? [];

    const handleSendToBot = () =>
        confirm({
            title: 'Naar de DGR-bot sturen?',
            description:
                'Het resultaat van de automatische DGR-controle kan andere services beïnvloeden. Weet je zeker dat je deze werknemer naar de DGR-bot wilt sturen?',
            confirmLabel: 'Versturen',
            onConfirm: close => {
                router.post(
                    `/customers/${employee.customer_id}/employees/${employee.id}/dgr/bot`,
                    {},
                    {
                        reset: ['employee', 'navigation'],
                        preserveState: true,
                        preserveScroll: true,
                        onFinish: close,
                    }
                );
            },
        });

    return (
        <>
            <Segment>
                <SegmentHeader
                    header='Doelgroepregister'
                    subheader={
                        employee?.dgr?.last_sent_to_bot_at
                            ? `Laatst naar de DGR-bot gestuurd op ${localDateToMinutes(employee.dgr.last_sent_to_bot_at)}`
                            : 'Nog niet naar de DGR-bot gestuurd'
                    }
                >
                    {auth?.permissions?.includes('start dgr bot') && (
                        <Button onClick={handleSendToBot}>
                            <CpuChipIcon className='size-4' />
                            Controleer DGR
                        </Button>
                    )}
                </SegmentHeader>
                <SegmentContent>
                    <Table striped>
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell>
                                    Gecontroleerd op
                                </TableHeaderCell>
                                <TableHeaderCell textAlign='center'>
                                    Status
                                </TableHeaderCell>
                                <TableHeaderCell textAlign='center'>
                                    Schermafbeelding
                                </TableHeaderCell>
                                <TableHeaderCell textAlign='right'>
                                    Type check
                                </TableHeaderCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {checks.length === 0 && (
                                <NoDataRow numberOfColumns={4} />
                            )}

                            {checks.map(check => (
                                <TableRow key={check.id}>
                                    <TableCell>
                                        {localDate(check.checked_at)}
                                    </TableCell>
                                    <TableCell textAlign='center'>
                                        <StatusIcon status={check.status} />
                                    </TableCell>
                                    <TableCell textAlign='center'>
                                        {canDownload && check.document_id && (
                                            <Button
                                                as='a'
                                                href={`/dgr-identity-check/${check.id}/screenshot/download`}
                                                target='_blank'
                                                rel='noopener noreferrer'
                                                aria-label='Schermafbeelding downloaden'
                                            >
                                                <ArrowDownTrayIcon className='size-5' />
                                            </Button>
                                        )}
                                    </TableCell>
                                    <TableCell textAlign='right'>
                                        <Badge
                                            color={check.type_color}
                                            title={check.reason || ''}
                                        >
                                            {check.type_label}
                                        </Badge>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </SegmentContent>
            </Segment>

            {auth?.permissions?.includes('create manual dgr check') && (
                <DgrForm />
            )}
        </>
    );
};

DgrView.layout = page => <EmployeeShowLayout>{page}</EmployeeShowLayout>;

export default DgrView;
